import { ActionIcon, Box, Group, Modal, Text } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { CloudArrowUpIcon, PencilSimpleIcon, XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { message } from "../../utils/Message";
import { InvoiceAttachmentModalForm } from "./invoice-attachment-modal-form";
import classes from "./invoice-attachment-form.module.css";
import modalChrome from "../ui/app-modal.module.css";

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = [
  MIME_TYPES.pdf,
  MIME_TYPES.png,
  MIME_TYPES.jpeg,
  MIME_TYPES.webp,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

function fileExt(name?: string, url?: string) {
  const src = name || url || "";
  const m = /\.([a-z0-9]+)(?:\?|$)/i.exec(src);
  return (m?.[1] || "FILE").toUpperCase().slice(0, 4);
}

export function InvoiceAttachmentForm({ values, setFieldValue }: any) {
  const { t } = useTranslation("invoice");
  const [action, setAction] = useState<any>([]);
  const files = values?.attachments ?? [];

  /** One drop → one modal open with a unique uploadId (upload starts once in modal). */
  const openWithFile = (file?: File) => {
    if (file) setAction(["add", { pendingFile: file, uploadId: uuidv4() }]);
    else setAction(["add", {}]);
  };

  return (
    <Box>
      <Box className={classes.list}>
        <Dropzone
          className={classes.dropzone}
          onDrop={(dropped) => {
            if (dropped[0]) openWithFile(dropped[0]);
          }}
          onReject={() => message.error(t("common:uploadRejected"))}
          maxSize={MAX_SIZE}
          accept={ACCEPT}
          multiple={false}
          maxFiles={1}
        >
          <Group gap="sm" justify="center" style={{ pointerEvents: "none" }}>
            <CloudArrowUpIcon size={18} color="var(--mantine-color-gray-7)" />
            <Text fz="sm" c="gray.8">
              {t("productForm.dropzoneLabel")}{" "}
              <Text span c="gray.9" fw={600}>
                {t("productForm.dropzoneBrowse")}
              </Text>
              {" · "}
              {t("productForm.dropzoneHint")}
            </Text>
          </Group>
        </Dropzone>

        {files.map((file: any) => (
          <Box key={file.id} className={classes.fileRow}>
            <Box component="span" className={classes.typeBadge}>
              {fileExt(file.name, file.url)}
            </Box>
            <Box flex={1} miw={0}>
              <Text fz="sm" fw={500} lineClamp={1}>
                {file.name || "-"}
              </Text>
              {file.description ? (
                <Text fz="sm" c="dimmed" lineClamp={1}>
                  {file.description}
                </Text>
              ) : null}
            </Box>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={t("productForm.actionEdit")}
              onClick={() => setAction(["edit", file])}
            >
              <PencilSimpleIcon size={16} />
            </ActionIcon>
            <ActionIcon
              variant="transparent"
              color="gray"
              aria-label={t("productForm.actionRemove")}
              onClick={() =>
                setFieldValue(
                  "attachments",
                  files.filter((f: any) => f.id !== file.id),
                )
              }
            >
              <XIcon size={16} />
            </ActionIcon>
          </Box>
        ))}
      </Box>

      <Modal
        opened={action[0] === "add" || action[0] === "edit"}
        onClose={() => setAction([])}
        withCloseButton={false}
        size="md"
        classNames={{ body: modalChrome.modalBodyFlush }}
      >
        <InvoiceAttachmentModalForm
          action={action}
          setAction={setAction}
          values={values}
          setFieldValue={setFieldValue}
        />
      </Modal>
    </Box>
  );
}
